import type { MapModel } from '../engine/map/mapModel';
import {
    RESOURCE_KEYS,
    createStarterSettlement,
    createToolItem,
    applyPopulationLoss,
    ensureHouses,
    getHouseCapacity,
    setPopulation,
    type SettlementInventory,
} from '../engine/map/settlement';
import type { HexMapState } from '../engine/render/renderer';
import { getSettlementTierData } from '../engine/economy/settlementTiers';
import { cubeRound } from '../engine/hex/hexMath';
import { buildUnitRoster } from '../engine/battle/roster';

export function runFoundingExpeditions(model: MapModel, state: HexMapState): boolean {
    let changed = false;
    const toComplete = state.foundingExpeditions.filter(
        (expedition) => expedition.status === 'traveling',
    );
    if (toComplete.length === 0) return false;

    toComplete.forEach((expedition) => {
        expedition.progress += 1;
        if (expedition.progress < expedition.totalTicks) return;
        expedition.status = 'complete';
        expedition.completedAt = state.simulation.tick;
        const targetTile = model.tiles.find((tile) => tile.id === expedition.targetTileId);
        if (!targetTile) return;
        if (state.settlementByTileId[targetTile.id]) return;
        const sourceSettlement = state.settlementsById[expedition.fromSettlementId];
        if (!sourceSettlement) return;
        const encounterResult = resolveFoundingEncounters(
            expedition,
            sourceSettlement.tilePosition,
            targetTile.position,
            model,
            state,
        );
        if (encounterResult.failed) {
            expedition.status = 'failed';
            expedition.completedAt = state.simulation.tick;
            expedition.failedBy = encounterResult.failedBy;
            expedition.losses = encounterResult.losses;
            expedition.looted = encounterResult.looted;
            if (encounterResult.escortLosses > 0 && sourceSettlement) {
                applyPopulationLoss(sourceSettlement, encounterResult.escortLosses);
            }
            changed = true;
            return;
        }
        expedition.losses = encounterResult.losses;
        expedition.looted = encounterResult.looted;
        if (encounterResult.escortLosses > 0 && sourceSettlement) {
            applyPopulationLoss(sourceSettlement, encounterResult.escortLosses);
        }
        const tierData = getSettlementTierData(state.config.initial.settlement.tier);
        const regionTiles = model.getTilesInRadius(
            targetTile.position.q,
            targetTile.position.r,
            targetTile.position.s,
            tierData.radius,
        );
        const settlement = createStarterSettlement(targetTile, regionTiles);
        const requiredHouses = Math.ceil(expedition.population / getHouseCapacity());
        if (settlement.buildings.houses < requiredHouses) {
            settlement.buildings.houses = requiredHouses;
        }
        ensureHouses(settlement);
        setPopulation(settlement, expedition.population);
        const numericKeys = RESOURCE_KEYS.filter((key) => key !== 'tools') as Array<
            keyof Omit<SettlementInventory, 'tools'>
        >;
        numericKeys.forEach((key) => {
            settlement.inventory[key] = 0;
        });
        settlement.inventory.food = expedition.foodSupplies;
        numericKeys.forEach((key) => {
            if (key === 'food') return;
            settlement.inventory[key] = expedition.cargo[key] ?? 0;
        });
        settlement.inventory.tools = [];
        settlement.toolIdCounter = 0;
        for (let i = 0; i < expedition.toolsSupplies; i += 1) {
            settlement.inventory.tools.push(createToolItem(settlement));
        }
        if (sourceSettlement && !expedition.looted && expedition.escortWeaponsIssued > 0) {
            const roster = buildUnitRoster(state.config);
            const returnedWeapons = expedition.escortUnits.reduce((sum, group) => {
                const unit = roster.get(group.type);
                if (!unit) return sum;
                return sum + unit.weaponCost * group.count;
            }, 0);
            sourceSettlement.inventory.weapons += Math.min(
                expedition.escortWeaponsIssued,
                returnedWeapons,
            );
        }
        state.settlementsById[settlement.id] = settlement;
        state.settlementByTileId[settlement.tileId] = settlement;
        const tiles = regionTiles.length > 0 ? regionTiles : [targetTile];
        tiles.forEach((tile) => state.ownedTileIds.add(tile.id));
        tiles.forEach((tile) => state.discoveredTileIds.add(tile.id));
        changed = true;
    });

    return changed;
}

function resolveFoundingEncounters(
    expedition: HexMapState['foundingExpeditions'][number],
    origin: { q: number; r: number; s: number },
    target: { q: number; r: number; s: number },
    model: MapModel,
    state: HexMapState,
): {
    failed: boolean;
    failedBy?: string;
    losses: number;
    escortLosses: number;
    looted: boolean;
} {
    const rules = state.config.expedition.encounters;
    const path = cubeLine(origin, target);
    let remaining = expedition.population;
    let losses = 0;
    let escortLosses = 0;
    let looted = false;
    let failedBy: string | undefined;

    for (let i = 1; i < path.length; i += 1) {
        const cube = path[i];
        const tile = model.getTileByCube(cube.q, cube.r, cube.s);
        const tileId = tile?.id;
        if (!tileId) continue;
        const enemy = state.enemyForces[tileId];
        if (!enemy || enemy.defeatedAt) continue;
        const rng = mulberry32(hashSeed(state.config.map.seed ?? 0, expedition.id, tileId));
        if (rules.fatalFactions.includes(enemy.faction)) {
            const wipedEscort = countUnits(expedition.escortUnits);
            escortLosses += wipedEscort;
            expedition.escortUnits = [];
            losses += remaining;
            remaining = 0;
            looted = rules.stealAll || looted;
            expedition.foodSupplies = 0;
            expedition.toolsSupplies = 0;
            expedition.cargo = {};
            failedBy = enemy.faction;
            break;
        }
        const loss = randInt(rules.banditLossRange[0], rules.banditLossRange[1], rng);
        const { remainingLoss, escortLoss } = applyEscortLosses(expedition.escortUnits, loss);
        if (escortLoss > 0) {
            escortLosses += escortLoss;
        }
        const actualLoss = Math.min(remaining, remainingLoss);
        remaining -= actualLoss;
        losses += actualLoss;
        if (rules.stealAll && !looted) {
            looted = true;
            expedition.foodSupplies = 0;
            expedition.toolsSupplies = 0;
            expedition.cargo = {};
        }
        if (remaining <= 0) {
            failedBy = enemy.faction;
            break;
        }
    }

    expedition.population = Math.max(0, remaining);
    return {
        failed: remaining <= 0,
        failedBy,
        losses,
        escortLosses,
        looted,
    };
}

function countUnits(units: Array<{ count: number }>): number {
    return units.reduce((sum, group) => sum + group.count, 0);
}

function applyEscortLosses(
    escort: Array<{ count: number }>,
    loss: number,
): { remainingLoss: number; escortLoss: number } {
    let remainingLoss = Math.max(0, loss);
    let escortLoss = 0;
    for (let i = 0; i < escort.length && remainingLoss > 0; i += 1) {
        const group = escort[i];
        const take = Math.min(group.count, remainingLoss);
        group.count -= take;
        escortLoss += take;
        remainingLoss -= take;
    }
    for (let i = escort.length - 1; i >= 0; i -= 1) {
        if (escort[i].count <= 0) escort.splice(i, 1);
    }
    return { remainingLoss, escortLoss };
}

function cubeLine(a: { q: number; r: number; s: number }, b: { q: number; r: number; s: number }) {
    const distance = Math.max(Math.abs(a.q - b.q), Math.abs(a.r - b.r), Math.abs(a.s - b.s));
    const results: { q: number; r: number; s: number }[] = [];
    for (let i = 0; i <= distance; i += 1) {
        const t = distance === 0 ? 0 : i / distance;
        results.push(cubeRound(cubeLerp(a, b, t)));
    }
    return results;
}

function cubeLerp(
    a: { q: number; r: number; s: number },
    b: { q: number; r: number; s: number },
    t: number,
): { q: number; r: number; s: number } {
    return {
        q: a.q + (b.q - a.q) * t,
        r: a.r + (b.r - a.r) * t,
        s: a.s + (b.s - a.s) * t,
    };
}

function randInt(min: number, max: number, rng: () => number): number {
    const low = Math.min(min, max);
    const high = Math.max(min, max);
    return Math.floor(low + (high - low + 1) * rng());
}

function hashSeed(seed: number, id: string, tileId: string): number {
    let h = seed >>> 0;
    const str = `${id}:${tileId}`;
    for (let i = 0; i < str.length; i += 1) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

function mulberry32(seed: number): () => number {
    let t = seed >>> 0;
    return () => {
        t += 0x6d2b79f5;
        let x = Math.imul(t ^ (t >>> 15), 1 | t);
        x ^= x + Math.imul(x ^ (x >>> 7), 61 | x);
        return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
    };
}
